import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { CreateSbAdGroupForm } from '@/components/campaign-create/create-adgroup/CreateSbAdGroupForm';
import { Button } from '@/components/ui/Button';
import type { SpCampaignNormalized } from '@/types/spCampaigns';

export function CreateSbAdGroupModal({
  profileId,
  campaignId,
  campaign,
  isOpen,
  onClose,
}: {
  profileId: string;
  campaignId: string;
  campaign: SpCampaignNormalized;
  isOpen: boolean;
  onClose: () => void;
}) {
  const [discardPrompt, setDiscardPrompt] = useState(false);

  const requestClose = useCallback(() => {
    setDiscardPrompt(true);
  }, []);

  useEffect(() => {
    if (!isOpen) setDiscardPrompt(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !discardPrompt) return;
      event.preventDefault();
      setDiscardPrompt(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [discardPrompt, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-sb-ad-group-title"
        className="relative bg-surface rounded-xl w-full max-w-5xl my-6 shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-3 border-b border-border sticky top-0 bg-surface rounded-t-xl z-10">
          <div>
            <h2 id="new-sb-ad-group-title" className="text-lg font-semibold">
              New ad group
            </h2>
            <p className="text-sm text-foreground-muted mt-0.5">Collections for {campaign.name}</p>
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="text-chrome-label hover:text-foreground-muted mt-0.5"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-4 max-h-[min(80vh,52rem)] overflow-y-auto">
          <CreateSbAdGroupForm
            profileId={profileId}
            campaignId={campaignId}
            onSuccess={onClose}
            onCancel={requestClose}
          />
        </div>
        {discardPrompt ? (
          <div className="absolute inset-0 z-20">
            <div className="absolute inset-0 bg-black/40" aria-hidden />
            <div className="sticky top-0 z-10 flex h-dvh max-h-full items-center justify-center p-4">
              <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="discard-sb-ad-group-title"
                aria-describedby="discard-sb-ad-group-description"
                className="w-full max-w-sm rounded-xl border border-border bg-surface px-5 py-5 shadow-xl"
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden />
                  <div>
                    <h3 id="discard-sb-ad-group-title" className="text-base font-semibold text-foreground">
                      Discard this ad group?
                    </h3>
                    <p id="discard-sb-ad-group-description" className="mt-1 text-sm text-foreground-muted">
                      Anything you entered will be lost.
                    </p>
                  </div>
                </div>
                <div className="mt-5 flex items-center justify-end gap-2">
                  <Button variant="secondary" size="sm" onClick={() => setDiscardPrompt(false)}>
                    Keep editing
                  </Button>
                  <Button variant="warning" size="sm" onClick={onClose}>
                    Discard
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
